/**
 * SelectrUI.tsx - Universal Selection UI Component
 * 
 * CHANGE HISTORY:
 * - 251115_2200 [pending] danielbeer: Renamed from Selectr.tsx, separated UI from state management
 * - 251102_0906 [pending] danielbeer: Initial creation with full Listr integration
 * 
 * Universal selection UI for OpCentral - integrates with Listr, Organizr, and custom data sources
 * For selection state management, see SelectrManager and HierarchicalSelectrManager
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import './SelectrUI.css';
import type {
  SelectrConfig,
  SelectrItem,
  SelectrState,
  SelectrResult
} from './types';

export interface SelectrProps extends SelectrConfig {
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  value?: SelectrItem | SelectrItem[];
}

/**
 * Selectr Component
 * 
 * Universal selection component that works with:
 * - Listr (dynamic lists)
 * - Organizr (static lists)
 * - Arrays (simple data)
 * - Async functions (custom loaders)
 */
export const Selectr: React.FC<SelectrProps> = ({
  source,
  listId,
  items: initialItems,
  loadItems: customLoadItems,
  mode = 'single',
  uiMode = 'dropdown',
  displayConfig,
  searchable = true,
  grouped = false,
  clearable = false,
  closeOnSelect = true,
  placeholder = 'Select...',
  emptyMessage = 'No items found',
  maxHeight = 300,
  width,
  onSelect,
  onChange,
  onSearch,
  onOpen,
  onClose,
  virtualized = false,
  async: isAsync = false,
  cache = true,
  debounce = 300,
  className,
  style,
  disabled = false,
  value
}) => {
  // State
  const [state, setState] = useState<SelectrState>({
    isOpen: false,
    isLoading: false,
    searchQuery: '',
    selectedItems: Array.isArray(value) ? value : value ? [value] : [],
    filteredItems: initialItems || [],
    focusedIndex: 0
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Load items based on source
  const loadItemsFromSource = async (): Promise<SelectrItem[]> => {
    switch (source) {
      case 'listr':
        // TODO: Integrate with Listr when available
        // const listr = getListr();
        // const result = await listr.getItems(listId!);
        // return mapListrItems(result.items, displayConfig);
        return [];
      
      case 'organizr':
        // TODO: Integrate with Organizr when available
        // const organizr = getOrganizr();
        // const list = organizr.getList(listId!);
        // return mapOrganizrItems(list.items, displayConfig);
        return [];
      
      case 'array':
        return initialItems || [];
      
      case 'async':
        if (customLoadItems) {
          return await customLoadItems();
        }
        return [];
      
      default:
        return [];
    }
  };

  // Load items on mount or when source changes
  useEffect(() => {
    const loadData = async () => {
      setState(prev => ({ ...prev, isLoading: true }));
      try {
        const loadedItems = await loadItemsFromSource();
        setState(prev => ({
          ...prev,
          filteredItems: loadedItems,
          isLoading: false
        }));
      } catch (error) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: (error as Error).message
        }));
      }
    };

    loadData();
  }, [source, listId]);

  // Filter items based on search query (with Filtr integration)
  const filteredItems = useMemo(() => {
    if (!state.searchQuery) {
      return state.filteredItems;
    }

    // Try to use Filtr if available
    try {
      const { getFiltr } = require('@opcentral/filtr');
      const filtr = getFiltr();
      
      const results = filtr.filter({
        query: state.searchQuery,
        items: state.filteredItems,
        config: {
          fuzzySearch: true,
          searchFields: ['label', 'description'],
          highlightMatches: false,
          minScore: 0.3
        }
      });
      
      return results.map(r => r.item);
    } catch (e) {
      // Fallback to simple filtering if Filtr not available
      const query = state.searchQuery.toLowerCase();
      return state.filteredItems.filter(item =>
        item.label.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
      );
    }
  }, [state.filteredItems, state.searchQuery]);

  // Group items if needed
  const groupedItems = useMemo(() => {
    if (!grouped) return null;

    const groups = new Map<string, SelectrItem[]>();
    filteredItems.forEach(item => {
      const groupKey = item.group || 'Other';
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(item);
    });

    return Array.from(groups.entries()).map(([label, items]) => ({
      id: label,
      label,
      items
    }));
  }, [filteredItems, grouped]);

  // Handle selection
  const handleSelect = (item: SelectrItem) => {
    if (item.disabled) return;

    let newSelected: SelectrItem[];
    
    if (mode === 'single') {
      newSelected = [item];
      if (closeOnSelect) {
        handleClose();
      }
    } else {
      const isSelected = state.selectedItems.some(s => s.id === item.id);
      if (isSelected) {
        newSelected = state.selectedItems.filter(s => s.id !== item.id);
      } else {
        newSelected = [...state.selectedItems, item];
      }
    }

    setState(prev => ({ ...prev, selectedItems: newSelected }));
    
    const result = mode === 'single' ? newSelected[0] : newSelected;
    onSelect?.(result);
    onChange?.(result);
  };

  // Handle search
  const handleSearch = (query: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setState(prev => ({ ...prev, searchQuery: query }));
      onSearch?.(query);
    }, debounce);
  };

  // Handle open/close
  const handleOpen = () => {
    setState(prev => ({ ...prev, isOpen: true }));
    onOpen?.();
    setTimeout(() => searchInputRef.current?.focus(), 0);
  };

  const handleClose = () => {
    setState(prev => ({ ...prev, isOpen: false, searchQuery: '', focusedIndex: 0 }));
    onClose?.();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!state.isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleOpen();
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        handleClose();
        break;
      
      case 'ArrowDown':
        e.preventDefault();
        setState(prev => ({
          ...prev,
          focusedIndex: Math.min(prev.focusedIndex + 1, filteredItems.length - 1)
        }));
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setState(prev => ({
          ...prev,
          focusedIndex: Math.max(prev.focusedIndex - 1, 0)
        }));
        break;
      
      case 'Enter':
        e.preventDefault();
        if (filteredItems[state.focusedIndex]) {
          handleSelect(filteredItems[state.focusedIndex]);
        }
        break;
    }
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    if (state.isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [state.isOpen]);

  // Render selected value
  const renderSelectedValue = () => {
    if (state.selectedItems.length === 0) {
      return <span className="selectr-placeholder">{placeholder}</span>;
    }

    if (mode === 'single') {
      return <span className="selectr-value">{state.selectedItems[0].label}</span>;
    }

    return (
      <span className="selectr-value">
        {state.selectedItems.length} selected
      </span>
    );
  };

  // Render item
  const renderItem = (item: SelectrItem, index: number) => {
    const isSelected = state.selectedItems.some(s => s.id === item.id);
    const isFocused = index === state.focusedIndex;

    return (
      <div
        key={item.id}
        className={`selectr-item ${isSelected ? 'selected' : ''} ${isFocused ? 'focused' : ''} ${item.disabled ? 'disabled' : ''}`}
        onClick={() => handleSelect(item)}
        role="option"
        aria-selected={isSelected}
      >
        {mode === 'multiple' && (
          <input
            type="checkbox"
            checked={isSelected}
            readOnly
            className="selectr-checkbox"
          />
        )}
        {item.icon && <span className="selectr-item-icon">{item.icon}</span>}
        <div className="selectr-item-content">
          <div className="selectr-item-label">{item.label}</div>
          {item.description && (
            <div className="selectr-item-description">{item.description}</div>
          )}
        </div>
      </div>
    );
  };

  // Render dropdown UI
  const renderDropdown = () => (
    <div
      ref={containerRef}
      className={`selectr selectr-dropdown ${className || ''}`}
      style={{ width, ...style }}
    >
      <div
        className={`selectr-trigger ${state.isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={disabled ? undefined : handleOpen}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={state.isOpen}
      >
        {renderSelectedValue()}
        <span className="selectr-arrow">▼</span>
      </div>

      {state.isOpen && (
        <div className="selectr-dropdown-content" style={{ maxHeight }}>
          {searchable && (
            <div className="selectr-search">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search..."
                onChange={(e) => handleSearch(e.target.value)}
                className="selectr-search-input"
              />
            </div>
          )}

          <div className="selectr-items" role="listbox">
            {state.isLoading ? (
              <div className="selectr-loading">Loading...</div>
            ) : filteredItems.length === 0 ? (
              <div className="selectr-empty">{emptyMessage}</div>
            ) : grouped && groupedItems ? (
              groupedItems.map(group => (
                <div key={group.id} className="selectr-group">
                  <div className="selectr-group-label">{group.label}</div>
                  {group.items.map((item, idx) => renderItem(item, idx))}
                </div>
              ))
            ) : (
              filteredItems.map((item, idx) => renderItem(item, idx))
            )}
          </div>
        </div>
      )}
    </div>
  );

  // Render based on UI mode
  switch (uiMode) {
    case 'dropdown':
      return renderDropdown();
    
    case 'list':
    case 'menu':
    case 'modal':
    case 'inline':
      // TODO: Implement other UI modes
      return renderDropdown();
    
    default:
      return renderDropdown();
  }
};

export default Selectr;
